import React from "react";
import { Button } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiCreditCardOutline, mdiPencilOutline } from "@mdi/js";
import CardShell from "./CardShell";
import SendButton from "./SendButton";
import JobStatusBadge from "app/components/common/JobStatusBadge";
import { formatMoney } from "app/containers/Jobs/utils/format";
import { computePaymentActions } from "app/containers/Jobs/utils/paymentActionRules";

// Each payment state allows exactly one action, so the footer never offers a
// call the backend would reject:
//
//   no_payment   Mark as Paid  +  Send Payment Request
//   unpaid       Cancel Request                        (Mark as Paid would 409)
//   paid_manual  Undo
//   paid_stripe  nothing — a real charge is reversed in Stripe, not here
//
// When no payment exists yet the amount falls back to the job's quote (design).
const PaymentCard = ({
  job,
  tier = "Solo",
  onEditQuote,
  onSendPaymentRequest,
  onMarkPaid,
  onCancelRequest,
  onUndoPayment,
  sendTrack = {},
  sending = false,
  busy = false,
}) => {
  const payment = job.payment || {};
  const amount = payment.amount != null ? payment.amount : job.job_setup?.quote;

  const { canMarkPaid, canCancelRequest, canUndo } = computePaymentActions({
    status: payment.status,
    paymentId: payment.id,
    tier,
  });

  const actions = (
    <>
      {canMarkPaid && (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy}
          onClick={onMarkPaid}
        >
          Mark as Paid
        </Button>
      )}
      {canCancelRequest && (
        <Button
          variant="outline-danger"
          size="sm"
          disabled={busy}
          onClick={() => onCancelRequest?.(payment.id)}
        >
          Cancel Request
        </Button>
      )}
      {canUndo && (
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={busy}
          onClick={() => onUndoPayment?.(payment.id)}
        >
          Undo
        </Button>
      )}
      <SendButton
        track={sendTrack}
        sending={sending}
        onClick={onSendPaymentRequest}
      >
        Send Payment Request
      </SendButton>
    </>
  );

  return (
    <CardShell
      icon={
        <Icon path={mdiCreditCardOutline} size={0.7} className="jd-card-icon" />
      }
      title="Payment"
      headerRight={<JobStatusBadge domain="payment" value={payment.status} />}
      className="jd-card--half"
      footerLeft={
        <span className="jd-card-footer-note">
          Edit quote
          <Icon
            path={mdiPencilOutline}
            size={0.5}
            className="jd-footer-edit"
            onClick={onEditQuote}
          />
        </span>
      }
      footerRight={actions}
    >
      <div className="jd-value-strong jd-payment-amount">
        {amount != null ? formatMoney(amount) : "-"}
      </div>
      {payment.request_url && (
        <a
          className="jd-link"
          href={payment.request_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          View payment link
        </a>
      )}
    </CardShell>
  );
};

export default PaymentCard;
